import {
  buildTestLabel,
  hasFeature,
  OPTION_TYPES,
  shouldHideImageTitles,
} from './questions'
import { buildCarryForwardFeature, CarryForwardFeature } from './carryForward'
import { Concept, getConcepts } from './concepts'
import {
  buildConceptDisplayLogic,
  buildOptionsDisplayLogic,
  buildQuestionDisplayLogic,
  DisplayLogic,
  DisplayLogicByResource,
} from './displayLogic'
import { Question as QuestionAPI } from 'types/glass-api/domainModels'
import { getRequireViewImagesError } from './requireViewing'
import { buildExclusiveOptionIDs } from './exclusive'
import { sanitizeHtml } from './richText'
import { buildRandomizeOptionsFeature, RandomizeFeature } from './randomize'

export type UnitType = 'none' | 'dollars' | 'percentage'

export type AllocationOptionImage = {
  carryForwardOptionId: string | null
  constraints: {
    max: number | null
    min: number | null
    requireView: boolean
  }
  features: { hideTitle: boolean }
  id: string
  image: { alt: string; publicID: string } & { viewed: boolean }
  sort: number
  title: string
  type: 'image'
}

export type AllocationOptionText = {
  carryForwardOptionId: string | null
  constraints: {
    max: number | null
    min: number | null
  }
  id: string
  sort: number
  title: string
  type: 'text'
}

export type Question = {
  concepts: Concept[]
  constraints: {
    carryOverTotalOptionId: string | null
    exclusiveOptionIDs: Set<string>
    requireConceptView: boolean
    requireImageViews: boolean
    stepIncrement: number
    totalChips: number
    useCarryOverOptionAsMax: boolean
  }
  directions: string
  display: {
    unitType: UnitType
  }
  features: {
    carryForward: CarryForwardFeature | undefined
    conceptDisplayLogic: DisplayLogicByResource
    displayLogic: DisplayLogic
    displayXOfY: boolean
    optionDisplayLogic: DisplayLogicByResource
    randomizeOptions: RandomizeFeature | undefined
  }
  id: string
  options: (AllocationOptionImage | AllocationOptionText)[]
  // The response is a mapping of option ID to allocated chip value
  response: Record<string, number>
  testing: { label: string }
  title: string
  type: 'allocation'
}

export type AllocationForm = {
  allocations: Record<string, number>
}

export function getQuestion({
  id,
  question,
  questionsConfiguration,
}: {
  id: string
  question: QuestionAPI
  questionsConfiguration: Record<number, QuestionAPI>
}): Question {
  const concepts = getConcepts({ question })

  // Parse options from API data with constraints
  const allocationSettings = question.allocationSettings
  const options: (AllocationOptionImage | AllocationOptionText)[] =
    question.options.map((option) => {
      const constraints = {
        max: allocationSettings?.maxPerOption ?? option.high,
        min: allocationSettings?.minPerOption ?? option.low,
      }
      const carryForwardOptionId = option.carryOverParentId
        ? `${option.carryOverParentId}`
        : null

      if (question.contentTypeId === OPTION_TYPES.IMAGE && option.dataUrl) {
        return {
          carryForwardOptionId,
          constraints: { ...constraints, requireView: option.viewRequired },
          features: { hideTitle: shouldHideImageTitles(question) },
          id: `${option.id}`,
          image: {
            alt: option.title,
            publicID: option.dataUrl.public_id,
            viewed: false,
          },
          sort: option.sort,
          title: option.title,
          type: 'image',
        } satisfies AllocationOptionImage
      }

      return {
        carryForwardOptionId,
        constraints,
        id: `${option.id}`,
        sort: option.sort,
        title: option.title,
        type: 'text',
      } satisfies AllocationOptionText
    })

  // Initialize response object with option IDs set to their minimum (or 0)
  const response: Record<string, number> = {}
  options.forEach((option) => {
    response[option.id] = option.constraints.min ?? 0
  })

  // Parse allocation constraints from API with defaults
  const stepIncrement = allocationSettings?.step ?? 1
  const totalChips = allocationSettings?.total ?? 100
  const carryOverTotalOptionId = allocationSettings?.carryOverTotalOptionId
    ? `${allocationSettings.carryOverTotalOptionId}`
    : null
  const useCarryOverOptionAsMax =
    allocationSettings?.useCarryOverOptionAsMax ?? false

  // Parse display configuration from API with defaults
  const unitType: UnitType =
    allocationSettings?.units === 'DOLLARS'
      ? 'dollars'
      : allocationSettings?.units === 'PERCENT'
        ? 'percentage'
        : 'none'

  return {
    concepts,
    constraints: {
      carryOverTotalOptionId,
      exclusiveOptionIDs: buildExclusiveOptionIDs({ question }),
      requireConceptView: false, // Allocation questions don't require concept viewing
      requireImageViews: hasFeature({ code: 'VAL01', question }),
      stepIncrement,
      totalChips,
      useCarryOverOptionAsMax,
    },
    directions: sanitizeHtml(question.description),
    display: {
      unitType,
    },
    features: {
      carryForward: buildCarryForwardFeature({
        question,
        questionsConfiguration,
      }),
      conceptDisplayLogic: buildConceptDisplayLogic({ question }),
      displayLogic: buildQuestionDisplayLogic({ question }),
      displayXOfY: false, // Allocation questions don't support Display X of Y
      optionDisplayLogic: buildOptionsDisplayLogic({ question }),
      randomizeOptions: buildRandomizeOptionsFeature({ question }),
    },
    id,
    options,
    response,
    testing: { label: buildTestLabel(question) },
    title: sanitizeHtml(question.title),
    type: 'allocation',
  }
}

/**
 * Formats a value according to the specified unit type
 * @param value - The numeric value to format
 * @param unitType - The unit type ('none', 'dollars', 'percentage')
 * @returns Formatted string with appropriate prefix/suffix
 */
export function formatValue(value: number, unitType: UnitType): string {
  switch (unitType) {
    case 'dollars':
      return `$${value}`
    case 'percentage':
      return `${value}%`
    case 'none':
    default:
      return `${value}`
  }
}

/**
 * Calculates the remaining chips available for allocation
 * @param totalChips - The total number of chips available
 * @param allocations - Record of option IDs to allocated chip values
 * @returns The number of chips remaining to be allocated
 */
export function calculateRemaining(
  totalChips: number,
  allocations: Record<string, number>,
): number {
  const current = calculateCurrent(allocations)
  return totalChips - current
}

/**
 * Calculates the current total of allocated chips
 * @param allocations - Record of option IDs to allocated chip values
 * @returns The sum of all allocated chip values
 */
export function calculateCurrent(allocations: Record<string, number>): number {
  return Object.values(allocations).reduce((sum, value) => sum + value, 0)
}

/**
 * Determines if the exclusive option should be disabled
 * @param remaining - The remaining allocations for the question
 * @param total - The total allocations for the question
 * @returns True if the exclusive option should be disabled
 */
export function isExclusiveDisabled(remaining: number, total: number): boolean {
  return remaining < total
}

/**
 * Determines if the minus button should be disabled
 * @param currentValue - The current allocation value for the option
 * @param minConstraint - The minimum constraint for the option (null if no constraint)
 * @returns True if the minus button should be disabled
 */
export function isMinusDisabled(
  currentValue: number,
  minConstraint: number | null,
): boolean {
  if (minConstraint !== null) {
    return currentValue <= minConstraint
  }
  return currentValue === 0
}

/**
 * Determines if the plus button should be disabled
 * @param remaining - The number of chips remaining to be allocated
 * @param currentValue - The current allocation value for the option
 * @param maxConstraint - The maximum constraint for the option (null if no constraint)
 * @returns True if the plus button should be disabled
 */
export function isPlusDisabled(
  remaining: number,
  currentValue: number,
  maxConstraint: number | null,
): boolean {
  // Disable if no chips remain
  if (remaining === 0) {
    return true
  }

  // Disable if max constraint is reached
  if (maxConstraint !== null && currentValue >= maxConstraint) {
    return true
  }

  return false
}

/**
 * Generates the label text for counter displays
 * @param type - The counter type ('total', 'remaining', 'current')
 * @param value - The numeric value to display
 * @param unitType - The unit type for formatting
 * @returns Formatted counter label string
 */
export function getCounterLabel(
  type: 'total' | 'remaining' | 'current',
  value: number,
  unitType: UnitType,
): string {
  const formattedValue = formatValue(value, unitType)

  switch (type) {
    case 'total':
      return `Total: ${formattedValue}`
    case 'remaining':
      return `Remaining: ${formattedValue}`
    case 'current':
      return `Current: ${formattedValue}`
    default:
      return `${formattedValue}`
  }
}

export type ValidationResult = {
  isValid: boolean
}

/**
 * Validates if the allocation is complete and meets all constraints
 * @param question - The allocation question
 * @param allocations - Record of option IDs to allocated chip values
 * @returns Validation result with isValid flag and error messages
 */
export function canContinue(
  question: Question,
  allocations: Record<string, number>,
): ValidationResult {
  const remaining = calculateRemaining(
    question.constraints.totalChips,
    allocations,
  )
  // Check per-option min/max constraints
  let optionConstraintsMet = true
  for (const option of question.options) {
    const value = allocations[option.id] || 0

    if (option.constraints.min !== null && value < option.constraints.min) {
      optionConstraintsMet = false
    }

    if (option.constraints.max !== null && value > option.constraints.max) {
      optionConstraintsMet = false
    }
  }

  return {
    isValid:
      remaining === 0 &&
      optionConstraintsMet &&
      !getRequireViewImagesError({ question }),
  }
}

/**
 * Resolves the carry-over total for an allocation question. When `carryOverTotalOptionId`
 * is set, the total chips for this question is determined by the value the respondent
 * allocated to that option in a previous allocation question.
 *
 * @param nextQuestion - The allocation question to resolve
 * @param questions - All questions (with their current responses)
 * @returns The question with the resolved total chips (and re-initialized response)
 */
export function resolveCarryOverTotal({
  nextQuestion,
  questions,
}: {
  nextQuestion: Question
  questions: Record<string, { type: string } & Record<string, unknown>>
}): Question {
  const { carryOverTotalOptionId } = nextQuestion.constraints

  if (!carryOverTotalOptionId) {
    return nextQuestion
  }

  // Find the previous allocation question that contains the referenced option
  const previousQuestion = Object.values(questions).find(
    (q): q is Question =>
      q.type === 'allocation' &&
      (q as Question).options.some((opt) => opt.id === carryOverTotalOptionId),
  )

  if (!previousQuestion) {
    return nextQuestion
  }

  const carriedTotal = previousQuestion.response[carryOverTotalOptionId] ?? 0

  // Re-initialize response with option minimums (or 0) since the total may have changed
  const response: Record<string, number> = {}
  nextQuestion.options.forEach((option) => {
    response[option.id] = option.constraints.min ?? 0
  })

  return {
    ...nextQuestion,
    constraints: {
      ...nextQuestion.constraints,
      totalChips: carriedTotal,
    },
    response,
  }
}

/**
 * Resolves the per-option max for an allocation question configured with
 * `useCarryOverOptionAsMax`. Each option carried forward from a previous allocation
 * question (identified by `carryForwardOptionId`) has its max bound set to the number
 * of chips the respondent allocated to the corresponding option in that previous
 * question. Options without a `carryForwardOptionId` (i.e. options a researcher added
 * beyond the carried-forward set) just get the total chips as their max, like normal.
 *
 * @param nextQuestion - The allocation question to resolve
 * @param questions - All questions (with their current responses)
 * @returns The question with each option's max bound resolved
 */
export function resolveCarryOverOptionMax({
  nextQuestion,
  questions,
}: {
  nextQuestion: Question
  questions: Record<string, { type: string } & Record<string, unknown>>
}): Question {
  if (!nextQuestion.constraints.useCarryOverOptionAsMax) {
    return nextQuestion
  }

  // Find the previous allocation question that contains any of the referenced options
  const previousQuestion = Object.values(questions).find(
    (q): q is Question =>
      q.type === 'allocation' &&
      nextQuestion.options.some(
        (option) =>
          option.carryForwardOptionId !== null &&
          (q as Question).options.some(
            (previousOption) =>
              previousOption.id === option.carryForwardOptionId,
          ),
      ),
  )

  const options = nextQuestion.options.map((option) => {
    const max = !option.carryForwardOptionId
      ? nextQuestion.constraints.totalChips
      : (previousQuestion?.response[option.carryForwardOptionId] ??
        option.constraints.max)

    if (option.type === 'image') {
      return {
        ...option,
        constraints: { ...option.constraints, max },
      } satisfies AllocationOptionImage
    }

    return {
      ...option,
      constraints: { ...option.constraints, max },
    } satisfies AllocationOptionText
  })

  return { ...nextQuestion, options }
}
